/**
 * Mark reconciliation — deterministic, and deliberately not the model's job.
 *
 * The pipeline learned this the expensive way: a paper whose marks do not add up
 * looks perfectly plausible, and a student only discovers it while sitting the
 * thing. Spec §12's stance is that honesty is a brand trait, so a paper that
 * fails to reconcile says so on its face rather than being quietly rendered.
 */

import type { Mismatch, Paper, PaperQuestion } from "./types";

export interface MarkDiscrepancy {
  where: string;
  expected: number;
  actual: number;
}

export interface Reconciliation {
  ok: boolean;
  statedTotal: number;
  countedTotal: number;
  discrepancies: MarkDiscrepancy[];
}

/** A question's marks come from its parts when it has any, else from itself. */
export function questionMarks(question: PaperQuestion): number {
  if (question.parts.length === 0) return question.marks;
  return question.parts.reduce((sum, part) => sum + part.marks, 0);
}

export function reconcile(paper: Paper): Reconciliation {
  const discrepancies: MarkDiscrepancy[] = [];

  for (const question of paper.questions) {
    if (question.parts.length === 0) continue;
    const fromParts = questionMarks(question);
    if (fromParts !== question.marks) {
      discrepancies.push({
        where: `Question ${question.number}`,
        expected: question.marks,
        actual: fromParts,
      });
    }
  }

  const countedTotal = paper.questions.reduce((sum, q) => sum + questionMarks(q), 0);
  if (countedTotal !== paper.total_marks) {
    discrepancies.push({
      where: "Paper total",
      expected: paper.total_marks,
      actual: countedTotal,
    });
  }

  return {
    ok: discrepancies.length === 0,
    statedTotal: paper.total_marks,
    countedTotal,
    discrepancies,
  };
}

/** A question or part whose mark scheme has fewer marking points than marks. */
export interface ShortScheme {
  /** "Question 03(b)", as the paper prints it. */
  where: string;
  marks: number;
  points: number;
}

const nonBlank = (points: string[]) => points.filter((p) => p.trim() !== "").length;

/**
 * Every marking point is worth one mark, so a question needs at least as many points as
 * marks. More points than marks is how real schemes say "any N from" these, and is fine;
 * fewer leaves marks with nothing to award them by. Generation settles shortfalls before
 * a paper is stored, so this finds them only on papers written before it did.
 */
export function shortSchemes(question: PaperQuestion): ShortScheme[] {
  const number = String(question.number).padStart(2, "0");
  const targets =
    question.parts.length === 0
      ? [{ label: "", marks: question.marks, points: question.marking_points }]
      : question.parts.map((p) => ({ label: p.label, marks: p.marks, points: p.marking_points }));
  return targets
    .filter((t) => nonBlank(t.points) < t.marks)
    .map((t) => ({
      where: `Question ${number}${t.label}`,
      marks: t.marks,
      points: nonBlank(t.points),
    }));
}

/** A shortfall in the words a student or teacher already uses. */
export function shortSchemeLine(s: ShortScheme): string {
  const marks = `${s.marks} ${s.marks === 1 ? "mark" : "marks"}`;
  if (s.points === 0)
    return `${s.where} is worth ${marks} but the mark scheme has no points for it.`;
  const points = `${s.points} ${s.points === 1 ? "point" : "points"}`;
  return `${s.where} is worth ${marks} but the mark scheme lists ${points}.`;
}

/** "9" → "09": questions are numbered as the boards print them. */
function questionLabel(q: string | null): string {
  if (q === null) return "?";
  return /^\d$/.test(q) ? `0${q}` : q;
}

/**
 * Generation's reconciliation in the student's words (API_CHANGES 5): sums it corrected
 * before the paper was stored, as a quiet note. Marking points are not repeated here —
 * the paper's own check reads them from the questions themselves.
 */
export function reconciliationNotes(found: Mismatch[] | undefined): { corrected: string[] } {
  const corrected: string[] = [];
  for (const m of found ?? []) {
    if (m.kind === "paper_total") {
      corrected.push(
        `Marks were corrected so they add up — cover stated ${m.stated}, questions total ${m.counted}.`,
      );
    } else if (m.kind === "question_parts") {
      corrected.push(
        `Marks were corrected so they add up — question ${questionLabel(m.question)} stated ${m.stated}, its parts total ${m.counted}.`,
      );
    }
  }
  return { corrected };
}
